import { useMemo, useState } from 'react';
import { Palette, Star, X, Save, ShieldCheck, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { COLOR } from '@/constants/testIds';
import { thumbUrl, displayName } from '@/components/color/colorUtils';

const selectCls = 'h-9 border-[hsl(var(--lxra-border))] bg-[#141416] text-xs text-[hsl(var(--lxra-fg))] focus:ring-[hsl(var(--lxra-gold))]';
const contentCls = 'bg-[#141416] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))]';
const prefKey = (p) => p.toLowerCase().replace(/[^a-z0-9]+/g, '-');

const PARAM_ORDER = ['temperature', 'tint', 'exposure', 'contrast', 'highlights', 'shadows', 'saturation', 'shadow_tint', 'highlight_tint', 'greens', 'blues'];

export const DirectionPanel = ({ data, options, profiles, onUpdate, onSaveStyle, busy }) => {
  const { project, profile, effective_params: params, images } = data;
  const direction = project.direction || {};
  const prefs = direction.preferences || [];
  const [saveOpen, setSaveOpen] = useState(false);
  const [styleName, setStyleName] = useState('');
  const referenceIds = project.reference_image_ids;
  const refs = useMemo(
    () => (referenceIds || []).map((id) => images.find((i) => i.id === id)).filter(Boolean),
    [referenceIds, images],
  );
  const optionParams = options?.params;
  const paramLabel = useMemo(() => Object.fromEntries((optionParams || []).map((p) => [p.key, p])), [optionParams]);
  const presetProfiles = useMemo(() => profiles.filter((p) => p.kind === 'preset'), [profiles]);
  const customProfiles = useMemo(() => profiles.filter((p) => p.kind === 'custom'), [profiles]);
  const activeParamKeys = useMemo(() => PARAM_ORDER.filter((k) => Math.abs(params?.[k] || 0) >= 0.05), [params]);

  const setDirection = (patch) => onUpdate({ direction: { style: direction.style || 'Natural', mood: direction.mood || 'Neutral', preferences: prefs, custom: direction.custom || '', ...patch } });
  const togglePref = (p) => setDirection({ preferences: prefs.includes(p) ? prefs.filter((x) => x !== p) : [...prefs, p] });

  return (
    <section data-testid={COLOR.directionPanel} className="rounded-2xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="flex items-center gap-2 text-xs uppercase tracking-[0.12em] text-[hsl(var(--lxra-muted))]"><Palette className="h-4 w-4 text-[hsl(var(--lxra-gold))]" /> Creative direction</p>
          <p className="mt-1 text-sm text-[hsl(var(--lxra-fg))]">Tell the engine the look you're after. Recommendations update instantly; nothing is applied until you approve it.</p>
        </div>
        <div className="flex items-center gap-2.5">
          <Label htmlFor="skin-protect" className="flex items-center gap-1.5 text-xs text-[hsl(var(--lxra-fg))]"><ShieldCheck className="h-3.5 w-3.5 text-[hsl(var(--lxra-gold))]" /> Skin-tone protection</Label>
          <Switch id="skin-protect" data-testid={COLOR.skinSwitch} checked={project.skin_protection !== false} onCheckedChange={(v) => onUpdate({ skin_protection: v })} disabled={busy} className="data-[state=checked]:bg-[hsl(var(--lxra-gold))]" />
        </div>
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-[1fr_1fr_1.4fr]">
        <div className="space-y-3">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.14em] text-[hsl(var(--lxra-muted))]">Style</Label>
            <Select value={direction.style || 'Natural'} onValueChange={(v) => setDirection({ style: v })} disabled={busy}>
              <SelectTrigger data-testid={COLOR.styleSelect} className={`${selectCls} mt-1`}><SelectValue /></SelectTrigger>
              <SelectContent className={contentCls}>{(options?.direction?.styles || []).map((s) => <SelectItem key={s} value={s} className="text-xs">{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.14em] text-[hsl(var(--lxra-muted))]">Mood</Label>
            <Select value={direction.mood || 'Neutral'} onValueChange={(v) => setDirection({ mood: v })} disabled={busy}>
              <SelectTrigger data-testid={COLOR.moodSelect} className={`${selectCls} mt-1`}><SelectValue /></SelectTrigger>
              <SelectContent className={contentCls}>{(options?.direction?.moods || []).map((s) => <SelectItem key={s} value={s} className="text-xs">{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {(direction.style === 'Custom' || prefs.includes('Custom')) && (
            <Input data-testid={COLOR.customInput} value={direction.custom || ''} onChange={(e) => setDirection({ custom: e.target.value })} placeholder="Describe the look in your own words" maxLength={400} className={`${selectCls} h-9`} />
          )}
        </div>

        <div>
          <Label className="text-[10px] uppercase tracking-[0.14em] text-[hsl(var(--lxra-muted))]">Colour preferences</Label>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {(options?.direction?.preferences || []).map((p) => {
              const on = prefs.includes(p);
              return (
                <button key={p} type="button" data-testid={COLOR.prefChip(prefKey(p))} aria-pressed={on} disabled={busy} onClick={() => togglePref(p)}
                  className={`rounded-full border px-2.5 py-1 text-[11px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--lxra-gold))] ${on ? 'border-[hsl(var(--lxra-gold))] bg-[hsl(var(--lxra-gold))]/15 text-[hsl(var(--lxra-gold))]' : 'border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-muted))] hover:border-[hsl(var(--lxra-gold))]/50 hover:text-[hsl(var(--lxra-fg))]'}`}>
                  {p}
                </button>
              );
            })}
          </div>
          <p className="mt-2 text-[10px] text-[hsl(var(--lxra-muted))]">Chips nudge the profile below; the profile carries the full recipe.</p>
        </div>

        <div className="space-y-3">
          <div>
            <div className="flex items-center justify-between">
              <Label className="text-[10px] uppercase tracking-[0.14em] text-[hsl(var(--lxra-muted))]">Color profile</Label>
              <Button size="sm" variant="ghost" data-testid={COLOR.saveStyleButton} onClick={() => { setStyleName(''); setSaveOpen(true); }} disabled={busy} className="h-6 px-2 text-[10px] uppercase tracking-[0.08em] text-[hsl(var(--lxra-muted))] hover:text-[hsl(var(--lxra-gold))]"><Save className="mr-1 h-3 w-3" /> Save as My Style</Button>
            </div>
            <Select value={project.profile_id || ''} onValueChange={(v) => onUpdate({ profile_id: v })} disabled={busy}>
              <SelectTrigger data-testid={COLOR.profileSelect} className={`${selectCls} mt-1`}><SelectValue placeholder="Choose a profile" /></SelectTrigger>
              <SelectContent className={contentCls}>
                {presetProfiles.map((p) => <SelectItem key={p.id} value={p.id} className="text-xs">{p.name}</SelectItem>)}
                {profiles.some((p) => p.kind === 'custom') && <div className="px-2 pt-2 pb-1 text-[10px] uppercase tracking-[0.12em] text-[hsl(var(--lxra-muted))]">My styles</div>}
                {customProfiles.map((p) => <SelectItem key={p.id} value={p.id} className="text-xs">{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
            {profile?.description && <p data-testid={COLOR.profileDesc} className="mt-1.5 text-[11px] leading-snug text-[hsl(var(--lxra-muted))]">{profile.description}</p>}
          </div>
          <div data-testid={COLOR.paramsSummary} className="flex flex-wrap gap-1">
            {activeParamKeys.map((k) => {
              const v = params[k];
              const spec = paramLabel[k] || {};
              return <span key={k} className="rounded-md border border-[hsl(var(--lxra-border))] bg-[#141416] px-1.5 py-0.5 text-[10px] text-[hsl(var(--lxra-fg))]">{spec.label || k}: <span className="text-[hsl(var(--lxra-gold))]">{v > 0 ? spec.pos : spec.neg}</span></span>;
            })}
            {activeParamKeys.length === 0 && <span className="text-[10px] text-[hsl(var(--lxra-muted))]">Neutral recipe — corrective only.</span>}
          </div>
        </div>
      </div>

      <div className="mt-4 border-t border-[hsl(var(--lxra-border))] pt-3">
        <div className="flex flex-wrap items-center gap-2">
          <Label className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.14em] text-[hsl(var(--lxra-muted))]"><Star className="h-3.5 w-3.5 text-[hsl(var(--lxra-gold))]" /> Reference frames</Label>
          <div data-testid={COLOR.referenceList} className="flex flex-wrap items-center gap-2">
            {refs.length === 0 && <span className="text-[11px] text-[hsl(var(--lxra-muted))]">None yet — open a photo and click “Use as reference”. Its balance becomes the target for the whole set.</span>}
            {refs.map((r, i) => (
              <span key={r.id} data-testid={COLOR.referenceChip(r.id)} className="inline-flex items-center gap-1.5 rounded-full border border-[hsl(var(--lxra-gold))]/50 bg-[#141416] pl-1 pr-2 py-0.5 text-[11px] text-[hsl(var(--lxra-fg))]">
                <img src={thumbUrl(r)} alt="" className="h-5 w-5 rounded-full object-cover" />
                <span className="max-w-[120px] truncate">{displayName(r, i)}</span>
                <button type="button" data-testid={COLOR.referenceClear(r.id)} onClick={() => onUpdate({ reference_image_ids: project.reference_image_ids.filter((id) => id !== r.id) })} className="text-[hsl(var(--lxra-muted))] hover:text-red-300" aria-label="Remove reference"><X className="h-3 w-3" /></button>
              </span>
            ))}
          </div>
          {busy && <Loader2 className="ml-auto h-3.5 w-3.5 animate-spin text-[hsl(var(--lxra-gold))]" />}
        </div>
      </div>

      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent className="border-[hsl(var(--lxra-border))] bg-[#0F0F10] text-[hsl(var(--lxra-fg))] sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl tracking-[0.03em]">Save as My Style</DialogTitle>
            <DialogDescription className="text-xs text-[hsl(var(--lxra-muted))]">Stores the current profile recipe, style, mood and preferences as a reusable profile you can pick on any project.</DialogDescription>
          </DialogHeader>
          <Input data-testid={COLOR.saveStyleName} value={styleName} onChange={(e) => setStyleName(e.target.value)} placeholder="e.g. My Style — Warm Film" maxLength={80} className={`${selectCls} h-10`} autoFocus />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSaveOpen(false)} className="text-[hsl(var(--lxra-muted))]">Cancel</Button>
            <Button data-testid={COLOR.saveStyleConfirm} disabled={!styleName.trim() || busy} onClick={async () => { await onSaveStyle(styleName.trim()); setSaveOpen(false); }} className="bg-[hsl(var(--lxra-gold))] text-[#0A0A0A] hover:bg-[hsl(var(--lxra-gold-hover))]">Save profile</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
};
